import { PALETTE_A, PALETTE_B, withAliases } from "./bluetooth"

export const HOLD_TYPES = ["START", "MIDDLE", "FINISH", "FOOT"]
export const SLOTS = ["A", "B"]

const pickHoldTypes = (palette) =>
  Object.fromEntries(HOLD_TYPES.map((type) => [type, palette[type]]))

export const DEFAULT_PALETTES = {
  A: pickHoldTypes(PALETTE_A),
  B: pickHoldTypes(PALETTE_B),
}

// Clamp user input to a valid color byte, falling back to the default
// while the field is empty or mid-edit
export const toColorByte = (value, fallback) => {
  const n = Number(value)
  return value !== "" && Number.isFinite(n)
    ? Math.max(0, Math.min(255, Math.round(n)))
    : fallback
}

// Snap any stored value (old free-typed bytes included) onto one of the
// 64 dropdown options by dropping the LED-position bits
export const toColorOption = (value, fallback) =>
  toColorByte(value, fallback) & 252

// The gym's shared palette for a slot, merged over the defaults so a
// slot with no stored colors (or a partial row) still yields every hold
// type. Only hold-type keys are kept; server rows also carry updated_at.
export const paletteValuesForSlot = (palettes, slot) =>
  Object.fromEntries(
    HOLD_TYPES.map((type) => [
      type,
      palettes?.[slot]?.[type] ?? DEFAULT_PALETTES[slot][type],
    ]),
  )

export const buildPalette = (palettes, slot) => {
  const values = paletteValuesForSlot(palettes, slot)
  return withAliases(
    Object.fromEntries(
      HOLD_TYPES.map((type) => [
        type,
        toColorByte(values[type], DEFAULT_PALETTES[slot][type]),
      ]),
    ),
  )
}

/**
 * The `{climb, palette}` entries to light for a gym's current slots,
 * using the gym's shared colors.
 */
export const slotEntriesFor = (gym) =>
  SLOTS.map((slot) => ({
    climb: gym?.slots?.[slot]?.climb,
    palette: buildPalette(gym?.palettes, slot),
  })).filter((entry) => entry.climb)

/**
 * Changes whenever any slot is set or cleared, or any slot's colors
 * change, so a follower can tell a fresh poll result apart from what it
 * already sent to the board.
 */
export const updatedKeyFor = (gym) =>
  SLOTS.map(
    (slot) =>
      `${gym?.slots?.[slot]?.updated_at ?? ""}:${HOLD_TYPES.map(
        (type) => paletteValuesForSlot(gym?.palettes, slot)[type],
      ).join(",")}`,
  ).join("|")
